
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Building, FileText, Printer } from 'lucide-react';
import TerviLogo from '../components/common/TerviLogo';

const InvoiceDetailScreen = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { invoice, chatInfo } = location.state || {};

    useEffect(() => {
        if (!invoice || !chatInfo) {
            navigate(-1);
        }
    }, [invoice, chatInfo, navigate]);

    if (!invoice || !chatInfo) {
        return null;
    }

    const clientDetails = chatInfo.participantDetails[chatInfo.clientId];
    const serviDetails = chatInfo.participantDetails[chatInfo.serviId];
    const finalServiName = serviDetails?.companyName || serviDetails?.name || "Prestador";

    const renderLineItem = (description, price, key) => (
        <tr key={key}>
            <td className="py-2 px-4 border-b border-gray-200 text-gray-700">{description}</td>
            <td className="py-2 px-4 border-b border-gray-200 text-right font-medium">R$ {(parseFloat(price) || 0).toFixed(2)}</td>
        </tr>
    );

    const handlePrint = () => window.print();

    return (
        <div className="bg-gray-100 min-h-screen">
            <header className="p-4 bg-white shadow-md flex justify-between items-center print:hidden">
                <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-200">
                    <ArrowLeft className="w-6 h-6 text-gray-700" />
                </button>
                <h1 className="text-lg font-bold text-gray-800">Detalhe da Cobrança</h1>
                <button onClick={handlePrint} className="p-2 rounded-full hover:bg-gray-200">
                    <Printer className="w-6 h-6 text-blue-600" />
                </button>
            </header>

            <main className="p-4 md:p-8 bg-white max-w-4xl mx-auto my-4 rounded-lg shadow-lg">
                <div className="flex justify-between items-start pb-4 mb-6 border-b-2 border-gray-300">
                    <TerviLogo size={40} />
                    <div className="text-right">
                        <h2 className="text-2xl font-bold">{finalServiName}</h2>
                        <p className="text-gray-500">{serviDetails?.name}</p>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-8 mb-8">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-500 uppercase flex items-center mb-1"><User className="w-4 h-4 mr-2"/>Cobrança para</h3>
                        <p className="font-medium text-gray-800">{clientDetails?.name}</p>
                    </div>
                    <div className="text-right">
                        <h3 className="text-sm font-semibold text-gray-500 uppercase mb-1">Data da Finalização</h3>
                        <p className="font-medium text-gray-800">{new Date().toLocaleDateString('pt-BR')} </p>
                    </div>
                </div>

                <h3 className="font-bold mb-2 text-gray-700 flex items-center"><FileText className="w-5 h-5 mr-2"/>Resumo do Serviço</h3>
                <table className="w-full text-sm mb-8">
                    <thead>
                        <tr>
                            <th className="text-left font-semibold uppercase text-gray-500 p-2 bg-gray-100 rounded-l-lg">Descrição</th>
                            <th className="text-right font-semibold uppercase text-gray-500 p-2 bg-gray-100 rounded-r-lg">Valor</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(invoice.includedQuotes || []).flatMap((q, i) =>
                            (q.items || []).map((item, j) => renderLineItem(item.description, item.price, `quote-${i}-${j}`))
                        )}
                        {(invoice.newItems || []).map((item, i) => renderLineItem(item.description, item.price, `new-${i}`))}
                        {invoice.discount > 0 && (
                            <tr>
                                <td className="py-2 px-4 border-b border-gray-200 font-semibold text-red-600">Desconto Aplicado</td>
                                <td className="py-2 px-4 border-b border-gray-200 text-right font-bold text-red-600">- R$ {invoice.discount.toFixed(2)}</td>
                            </tr>
                        )}
                    </tbody>
                </table>

                <div className="flex justify-end">
                    <div className="w-full max-w-xs">
                        <div className="flex justify-between items-center py-2">
                            <span className="text-gray-600 font-medium">Subtotal</span>
                            <span className="text-gray-800">R$ {(invoice.total + invoice.discount).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                            <span className="text-red-600 font-medium">Desconto</span>
                            <span className="text-red-600">- R$ {invoice.discount.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center py-3 mt-2 border-t-2 border-gray-800">
                            <span className="font-bold text-lg">VALOR TOTAL</span>
                            <span className="font-bold text-xl text-blue-600">R$ {invoice.total.toFixed(2)}</span>
                        </div>
                    </div>
                </div>
                 <footer className="text-center text-xs text-gray-400 mt-12 pt-4 border-t print:block hidden">
                    Este documento é um resumo da cobrança e não possui valor fiscal.
                </footer>
            </main>
        </div>
    );
};

export default InvoiceDetailScreen;
